// Module 2 — List Your Income
// ./index.ts owns this module's key and display name; this file owns its
// action items.

// Libs
import { useParams } from 'react-router';

// RDS
import Heading from '@ramsey-design-system/heading';
import { VideoOutlinedIcon } from '@ramsey-design-system/icons';
import Text from '@ramsey-design-system/text';

// Components
import DiscussionQuestions, {
  DiscussionQuestion
} from '../DiscussionQuestions';
import HLSVideoPlayer from '~fpu/shared/components/HLSVideoPlayer';
import QrCodePrompt from '../QrCodePrompt';

// Utils
import { PATHS } from '~fpu/config/constants';
import storeConfig from '~fpu/config/storeConfig';

// Types
import { WorkshopActionItemDefinition } from '../../types';

function TheRamseyShowClip() {
  return (
    <HLSVideoPlayer
      src="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/list-your-income.mp4"
      thumbnail="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/list-your-income.jpg"
    />
  );
}

function TypesOfIncome() {
  return (
    <div className="flex flex-col gap-4">
      <Text className="text-4 leading-lg">
        Before we can tell our money where to go, we need to know exactly how
        much is coming in.
      </Text>

      <div>
        <Heading level="3" size="small" className="mb-1">
          Regular Income
        </Heading>
        <ul className="text-4 leading-lg ml-2 list-disc">
          <li>Primary job (salary or hourly take-home)</li>
          <li>Part-time job</li>
          <li>Side hustle or freelance work</li>
          <li>Self-employment / business income</li>
        </ul>
      </div>

      <div>
        <Heading level="3" size="small" className="mb-1">
          Household Income
        </Heading>
        <ul className="text-4 leading-lg ml-2 list-disc">
          <li>Spouse or partner&apos;s income</li>
          <li>Child support or alimony received</li>
        </ul>
      </div>

      <div>
        <Heading level="3" size="small" className="mb-1">
          Other Income
        </Heading>
        <ul className="text-4 leading-lg ml-2 list-disc">
          <li>Rental income</li>
          <li>Social Security or disability benefits</li>
          <li>Pension or retirement distributions</li>
          <li>Investment dividends or interest (if consistent)</li>
          <li>Veterans benefits</li>
        </ul>
      </div>
    </div>
  );
}

function RegularVsIrregular() {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <Heading level="3" size="small" className="mb-1">
          Regular Income
        </Heading>
        <Text className="text-4 leading-lg">
          Regular income hits your bank account on a predictable schedule in a
          predictable amount — same paycheck, same day, every time. Think
          salaried job, hourly wage with consistent hours, or a fixed pension.
        </Text>
      </div>

      <div>
        <Heading level="3" size="small" className="mb-1">
          Irregular Income
        </Heading>
        <Text className="text-4 leading-lg">
          Irregular income varies in amount, timing, or both — freelance work,
          commission, tips, seasonal jobs, side hustles, or rental income that
          fluctuates. You can still budget with it, but you use your lowest
          recent month as the baseline instead of an average.
        </Text>
      </div>
    </div>
  );
}

function ListYourIncomeActivity() {
  const { workshopId } = useParams() as { workshopId: string };
  const activityUrl = `${storeConfig.appUrl}${PATHS.MEMBER_DASHBOARD.BUDGET_EXERCISE(workshopId)}`;

  return (
    <QrCodePrompt
      activityUrl={activityUrl}
      slug="list-your-income-activity"
      dialogTitle="List Your Income Activity QR Code"
      linkLabel="Budget Activity"
    >
      <Text className="text-5 text-blue-90 leading-lg font-bold">
        Scan the QR code to start building your budget!
      </Text>
      <Text className="text-4 leading-lg">
        For this activity, focus on listing your income sources and identifying
        whether each one is regular or irregular.
      </Text>
      <img
        src="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/income-animation.gif"
        alt="Step 1 of 5, List Your Income"
        className="border-gray-20 mt-1 h-auto w-full rounded-[20px] border"
      />
    </QrCodePrompt>
  );
}

const DISCUSSION_QUESTIONS: DiscussionQuestion[] = [
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-1.png',
    text: 'What surprised you most when you listed all your income sources?'
  },
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-2.png',
    text: 'Is your income regular, irregular, or a mix of both — and how does that affect how you think about budgeting?'
  },
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-3.png',
    text: "Do you and your spouse or partner know each other's exact take-home pay?"
  }
];

function Discussion() {
  return <DiscussionQuestions questions={DISCUSSION_QUESTIONS} />;
}

const actionItems: WorkshopActionItemDefinition[] = [
  {
    title: 'Net vs. Gross Income',
    Content: TheRamseyShowClip,
    icon: VideoOutlinedIcon,
    duration: '5:45'
  },
  { title: 'Types of Income', Content: TypesOfIncome },
  { title: 'Regular vs. Irregular', Content: RegularVsIrregular },
  { title: 'List Your Income Activity', Content: ListYourIncomeActivity },
  { title: 'Discussion', Content: Discussion }
];

export default actionItems;
